import omitProps from '@hocs/omit-props';
import { isEmpty, map, pipe, prop, sort } from 'ramda';
import React from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { connect } from 'react-refetch';
import { compose, setDisplayName, withHandlers, withProps, withPropsOnChange } from 'recompose';
import { HelpLink, Text } from '../../../components/common';
import { UnsplashHelpLink } from '../../../components/common/UnsplashHelpLink';
import { FilterName } from '../../../constants/FilterName';
import { HELP_LINKS } from '../../../constants/HelpLinks';
import { Layout } from '../../../constants/Layout';
import { Orientation } from '../../../constants/Orientation';
import { MainSource } from '../../../constants/Source';
import { generateRandomGuid4 } from '../../../monitoring/utils';
import { FiltersBiClickOrigins } from '../../hooks/bi/useTrackSearchAndFiltersAnalytics/useTrackSearchAndFiltersAnalytics.types';
import { MediaManagerSDKFolder, MediaManagerSDKResponseData } from '../../media-manager-sdk';
import { getAdapter } from '../../media-manager-sdk/getAdapter';
import { withSearchAndFiltersAnalytics } from '../bi/withSearchAndFiltersAnalytics';
import { withTrackUnsplashClick } from '../bi/withTrackUnsplashClick';
import { UnsplashClickOrigin, UnsplashTarget } from '../bi/withTrackUnsplashClick/withTrackUnsplashClick.types';
import { ejectIntl } from '../ejectIntl';
import { withLayoutAndSort } from '../withLayoutAndSort';
import { withMediaManagerSDK } from '../withMediaManagerSDK';
import { withMMGRSettings, withoutMMGRSettings } from '../withMMGRSettings';
import {
  IDecodedFolderProps,
  IHandlers,
  IUnsplashFilters,
  IWithExtendedSourceInner,
  IWithExtendedSourceOuter,
} from './types';
import { decodeUnsplashFolderId, encodeBiCategory, encodeFolderId, getId } from './withUnsplash.utils';
import { getLocale } from '../../env/getLocale';

const emptyItemFilters = [];

interface IFilterItem {
  id: string;
  name: string;
}

export const withUnsplash = compose<IWithExtendedSourceInner, IWithExtendedSourceOuter>(
  setDisplayName('withUnsplash'),
  withTrackUnsplashClick,
  withLayoutAndSort({ layouts: [Layout.Gallery] }),
  withMediaManagerSDK({ source: MainSource.Unsplash }),
  withMMGRSettings,
  injectIntl,
  connect<any>(({ listFoldersMP, folderId, mmgrSettings, intl: { formatMessage } }) => ({
    unsplashCategoriesRequest: {
      comparison: 'one-time',
      value() {
        const unsplashAdapter = getAdapter(MainSource.Unsplash, mmgrSettings);
        const privateSiteAdapter = getAdapter(MainSource.SiteFiles, mmgrSettings);
        const correlationIdFromPrivateAdapter = privateSiteAdapter.correlationId.get();

        if (correlationIdFromPrivateAdapter) {
          unsplashAdapter.correlationId.set(correlationIdFromPrivateAdapter);
        } else {
          unsplashAdapter.correlationId.randomize();
        }

        return listFoldersMP(folderId);
      },
      then: (response: MediaManagerSDKResponseData<MediaManagerSDKFolder[]>) => {
        const locale = getLocale();
        return {
          value: pipe<typeof response, MediaManagerSDKFolder[], IFilterItem[], any>(
            prop('data'),
            map((category: MediaManagerSDKFolder) => ({
              id: getId(category),
              name: formatMessage({ id: `unsplash.categories.${category.name}` }),
            })),
            sort((a, b) => a.name.localeCompare(b.name, locale)),
          )(response),
        };
      },
    },
  })),
  withoutMMGRSettings,
  withProps(({ folderId }) => {
    const { orientation, category } = decodeUnsplashFolderId(folderId);

    return {
      orientation,
      category,
      folderId: encodeFolderId({ orientation, category }),
    };
  }),
  withHandlers({
    trackUnsplashTermsClick:
      ({ trackUnsplashLinkClickClicked }) =>
      () =>
        trackUnsplashLinkClickClicked(UnsplashTarget.Terms, UnsplashClickOrigin.UnsplashTab),
    trackUnsplashLearMoreClick:
      ({ trackUnsplashLinkClickClicked }) =>
      () =>
        trackUnsplashLinkClickClicked(UnsplashTarget.LearnMore, UnsplashClickOrigin.UnsplashTab),
  }),
  withProps(
    ({ unsplashCategoriesRequest, intl: { formatMessage }, trackUnsplashTermsClick, trackUnsplashLearMoreClick }) => ({
      unsplashCategories: unsplashCategoriesRequest.fulfilled ? unsplashCategoriesRequest.value : [],
      unsplashOrientations: [
        { id: Orientation.All, name: formatMessage({ id: 'orientation.all' }) },
        { id: Orientation.Horizontal, name: formatMessage({ id: 'orientation.horizontal' }) },
        { id: Orientation.Vertical, name: formatMessage({ id: 'orientation.vertical' }) },
      ],
      unsplashDisclaimer: (
        <Text weight="thin" size="tiny" secondary light dataHook="unsplash-disclaimer">
          <FormattedMessage
            id="unsplash.disclaimer"
            values={{
              termsLink: (
                <UnsplashHelpLink messageId="unsplash.disclaimer.terms-link" onClick={trackUnsplashTermsClick} />
              ),
              learnMoreLink: (
                <HelpLink article={HELP_LINKS.LEARN_MORE_UNSPLASH} size="tiny" onClick={trackUnsplashLearMoreClick}>
                  <FormattedMessage id="unsplash.disclaimer.learn-more-link" />
                </HelpLink>
              ),
            }}
          />
        </Text>
      ),
    }),
  ),
  withSearchAndFiltersAnalytics,
  withHandlers({
    setCategory:
      ({ openFolderById, category: currentCategory, orientation, trackSearchAndFiltersStarted, unsplashCategories }) =>
      (category) => {
        if (category !== currentCategory) {
          openFolderById(
            encodeFolderId({
              orientation,
              category,
            }),
          );
          trackSearchAndFiltersStarted({
            correlationId: generateRandomGuid4(),
            clickOrigin: FiltersBiClickOrigins[FilterName.Category],
            target: category,
            filterOrientation: orientation,
            filterCategory: encodeBiCategory(category, unsplashCategories),
          });
        }
      },
    setOrientation:
      ({
        openFolderById,
        category,
        orientation: currentOrientation,
        trackSearchAndFiltersStarted,
        unsplashCategories,
      }) =>
      (orientation) => {
        if (orientation !== currentOrientation) {
          openFolderById(
            encodeFolderId({
              orientation,
              category,
            }),
          );
          trackSearchAndFiltersStarted({
            correlationId: generateRandomGuid4(),
            clickOrigin: FiltersBiClickOrigins[FilterName.Orientation],
            target: orientation,
            filterOrientation: orientation,
            filterCategory: encodeBiCategory(category, unsplashCategories),
          });
        }
      },
    clearAllExtendedFilters:
      ({ openFolderById }) =>
      () => {
        openFolderById(encodeFolderId({ orientation: Orientation.All, category: 'all' }));
      },
  }),
  withPropsOnChange<
    Partial<IWithExtendedSourceInner>,
    IUnsplashFilters & WrappedComponentProps & IHandlers & IDecodedFolderProps
  >(['unsplashCategories', 'category'], ({ unsplashCategories, category, setCategory, intl: { formatMessage } }) => {
    if (isEmpty(unsplashCategories)) {
      return { itemsFilters: emptyItemFilters };
    }

    return {
      itemsFilters: [
        {
          name: FilterName.Category,
          activeItemId: category,
          menuTitle: formatMessage({ id: 'unsplash.filter.category.title' }),
          menuItems: unsplashCategories,
          columnsCount: 2,
          onItemClick: (category) => () => setCategory(category),
        },
      ],
      title: formatMessage({ id: 'unsplash.title' }),
    };
  }),
  ejectIntl,
  omitProps('orientation', 'category'),
);
